import { useState, useRef, useCallback } from "react";
import { v4 as uuid } from "uuid";
import { RECEIVER_TYPE } from "@icoach/constants/dataEnums";
import { refIsRequiredError, isEqual } from "@util/utils";

/**
 * 專門管理新增訂單頁面中「接收者選擇與確認」邏輯的 Hook
 *
 * @param {function(): void} onReset - 重設後的回調
 * @returns {Object} 相關的狀態和處理器
 */
const useReceiverConfirmation = ({ onReset }) => {
	// refs
	const receiverTypeRef = useRef(null);
	const receiverFieldRef = useRef(null);

	// 接收者類型狀態
	const [receiverType, setReceiverType] = useState(0);

	// 接收者確認狀態
	const [isReceiverConfirmed, setIsReceiverConfirmed] = useState(false);

	// 重設 key：用於強制重新渲染組件 (個資安全)
	const [resetKey, setResetKey] = useState(uuid());

	// 取得接收者資料
	const getReceiverData = useCallback(
		() => {
			let result = {};
			// 接收者類型
			if (receiverTypeRef.current && receiverTypeRef.current.getResult()) {
				result.receiverType = receiverTypeRef.current.getResult();
			}
			// 接收者欄位資料
			if (receiverFieldRef.current && receiverFieldRef.current.getResult) {
				Object.assign(result, receiverFieldRef.current.getResult());
			}
			return result;
		},
		// eslint-disable-next-line
		[],
	);

	// 處理接收者類型變更
	const handleReceiverChange = useCallback(
		(e, n, v) => {
			setReceiverType(Number(v));
		},
		// eslint-disable-next-line
		[],
	);

	// 檢查接收者搜尋欄錯誤
	const checkReceiverError = useCallback(
		() => {
			return refIsRequiredError(receiverFieldRef, receiverTypeRef);
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 處理接收者重設
	 */
	const handleReset = useCallback(
		() => {
			setIsReceiverConfirmed(false);
			setReceiverType(0);
			// 使用 uuid 確保每次重設都是完全唯一的 key，不會有任何歷史痕跡（個資安全）
			setResetKey(uuid());
			if (onReset) {
				onReset();
			}
		},
		// eslint-disable-next-line
		[onReset],
	);

	/**
	 * 處理接收者確認
	 * @param {function(string|number): Promise<Object|null>} fetchCheckoutInfo - 取得結帳資訊的 API 函式
	 */
	const onReceiverConfirm = useCallback(
		async fetchCheckoutInfo => {
			// 驗證資料
			if (checkReceiverError()) return;

			// 取得接收者資料
			const receiverData = getReceiverData();
			const { receiverType, receiverID } = receiverData;

			// 只有會員類型需要調用 API
			if (isEqual(receiverType, RECEIVER_TYPE.MEMBER) && receiverID) {
				await fetchCheckoutInfo(receiverID);
			}

			// 更新確認狀態
			setIsReceiverConfirmed(true);
		},
		// eslint-disable-next-line
		[checkReceiverError, getReceiverData],
	);

	return {
		receiverType,
		isReceiverConfirmed,
		resetKey,
		receiverTypeRef,
		receiverFieldRef,
		handleReceiverChange,
		onReceiverConfirm,
		handleReset,
		getReceiverData,
	};
};

export default useReceiverConfirmation;
